import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, PlayIcon } from 'lucide-react';
import { brand } from '../../data/palette';
import { productById } from '../../data/products';
import type { CtaKind } from '../../data/journeys';
import type { ClinicalNeed } from '../../types/filtek';
interface NextStepPanelProps {
  kind: CtaKind;
  need: ClinicalNeed;
}

/** What a consultative next step looks like — no cart, no funnel. */
export function NextStepPanel({
  kind,
  need
}: NextStepPanelProps) {
  const product = productById(need.productId);
  const [requested, setRequested] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 18
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -12
  }} transition={{
    duration: 0.55,
    ease: [0.16, 1, 0.3, 1]
  }} className="border-l pl-6 sm:pl-8" style={{
    borderColor: need.accent
  }} data-id="element-122" data-mp="3mqz1qdwzsryx">
      {kind === 'technique' && <div data-id="element-123" data-mp="3rsy2rh59fcsp">
          <p className="label-mono text-[9px] text-mute" data-id="element-124" data-mp="3rpaccqv436i7">{need.technique} · chairside sequence</p>
          <ol className="mt-6 space-y-4" data-id="element-125" data-mp="12oial8eadkro">
            {need.techniqueSteps.map((step, index) => <li key={step} className="flex gap-4" data-id="element-126" data-mp="17nmjdxxatghh" data-mpi={index}>
                <span className="label-mono mt-1 text-[9px]" style={{
            color: need.accent
          }} data-id="element-127" data-mp="1bm2c8ig29j2t">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="max-w-xl text-base font-light leading-relaxed text-enamel/90" data-id="element-128" data-mp="1lljesr00nguk">
                  {step}
                </span>
              </li>)}
          </ol>
        </div>}

      {kind === 'product' && <div data-id="element-129" data-mp="1vce649s4d6mv">
          <p className="label-mono text-[9px] text-mute" data-id="element-130" data-mp="1vbw8p9tq0mdd">Specification</p>
          <p className="mt-5 text-xl font-light text-enamel" data-id="element-131" data-mp="1zbmypbbfaygu">{product.name}</p>
          <p className="mt-3 max-w-xl text-base font-light leading-relaxed text-mute" data-id="element-132" data-mp="24causbeie5hx">
            {product.descriptor}. {product.claim}
          </p>
          <dl className="mt-7 grid gap-x-10 gap-y-4 sm:grid-cols-2" data-id="element-133" data-mp="298rkha1cwrx4">
            {product.facts.map(fact => <div key={fact.label} data-id="element-134" data-mp="969uhln053yr">
                <dt className="label-mono text-[9px] text-mute/70" data-id="element-135" data-mp="4ab45si45bvp">{fact.label}</dt>
                <dd className="mt-1 text-sm font-light text-enamel" data-id="element-136" data-mp="iyxrohhw99y0">{fact.value}</dd>
              </div>)}
          </dl>
        </div>}

      {kind === 'evidence' && <div data-id="element-137" data-mp="rz44e15rq5w4">
          <p className="label-mono text-[9px] text-mute" data-id="element-138" data-mp="rykt33amifr2">Clinical evidence</p>
          <p className="mt-5 max-w-2xl text-lg font-light leading-relaxed text-enamel/90" data-id="element-139" data-mp="nxirwgx5btmg">
            {need.evidence}
          </p>
          <div className="mt-8 flex flex-wrap gap-10" data-id="element-140" data-mp="11xjqtqtuoqza">
            {[{
          value: '10 yr',
          label: 'Longest published follow-up'
        }, {
          value: '96%',
          label: 'Restorations rated clinically acceptable'
        }, {
          value: '2×',
          label: 'Independent studies referenced'
        }].map(stat => <div key={stat.label} data-id="element-141" data-mp="2yr4x730o4ei6">
                <p className="text-3xl font-light tracking-[-0.03em]" style={{
            color: need.accent
          }} data-id="element-142" data-mp="1e7j6czilwwk3">
                  {stat.value}
                </p>
                <p className="mt-2 max-w-[14rem] text-xs font-light text-mute" data-id="element-143" data-mp="14hzn3sqzf0op">{stat.label}</p>
              </div>)}
          </div>
        </div>}

      {kind === 'case' && <div data-id="element-144" data-mp="vh1lti7xh79t">
          <p className="label-mono text-[9px] text-mute" data-id="element-145" data-mp="vgiaikcs9h4r">Filmed clinical case</p>
          <div className="mt-5 grid gap-7 sm:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] sm:items-center" data-id="element-146" data-mp="10g4pay89f5fo">
            <div className="group relative overflow-hidden" data-id="element-147" data-mp="lj3wssi8v6g5">
              <img src={need.image} alt={`Clinical case: ${need.label.toLowerCase()}`} className="h-44 w-full object-cover opacity-80 transition-opacity duration-500 group-hover:opacity-100" data-id="element-148" data-mp="lkop0bh36dau" />
              <span className="absolute inset-0 flex items-center justify-center" data-id="element-149" data-mp="2kw7z4te4lrm3">
                <span className="flex h-14 w-14 items-center justify-center rounded-full" style={{
              backgroundColor: need.accent
            }} data-id="element-150" data-mp="ktgmnrdxqems">
                  <PlayIcon className="h-5 w-5 text-ink" aria-hidden="true" data-id="element-151" data-mp="bt1uc82jzz9s" />
                </span>
              </span>
            </div>
            <div data-id="element-152" data-mp="6sw14ngmy4pl">
              <p className="text-lg font-light leading-snug text-enamel" data-id="element-153" data-mp="6tfio32dir33">
                {need.label}: start to polish
              </p>
              <p className="mt-3 text-sm font-light leading-relaxed text-mute" data-id="element-154" data-mp="3t9jr7o9r3n6w">
                Dr. Elena Marsh walks through a single appointment using {product.shortName},
                narrated at working speed.
              </p>
              <p className="label-mono mt-5 text-[9px] text-mute/70" data-id="element-155" data-mp="3k9n0lh3swe97">7 min · unedited</p>
            </div>
          </div>
        </div>}

      {kind === 'contact' && <div data-id="element-156" data-mp="3aaxrodtvu453">
          <p className="label-mono text-[9px] text-mute" data-id="element-157" data-mp="3aae70pcul1nd">A practice-level conversation</p>
          <p className="mt-5 max-w-xl text-lg font-light leading-relaxed text-enamel/90" data-id="element-158" data-mp="3078w3z4juyc9">
            A Solventum specialist reviews your case mix with you and brings only what is relevant
            to {need.label.toLowerCase()} — samples included.
          </p>
          <button type="button" onClick={() => setRequested(true)} disabled={requested} className="mt-8 inline-flex items-center gap-3 border px-6 py-3 text-sm font-light transition-colors duration-500" style={{
        borderColor: need.accent,
        color: requested ? brand.ink : need.accent,
        backgroundColor: requested ? need.accent : 'transparent'
      }} data-id="element-159" data-mp="3a2u1m78tugqr">
            {requested && <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-160" data-mp="2w6gm33nuspex" />}
            {requested ? 'Requested — we will be in touch' : 'Request a conversation'}
          </button>
        </div>}
    </motion.div>;
}